import React, { Fragment, useCallback } from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { formatValue } from "../../components/Utils.js";
import { Tooltip } from 'react-tooltip'
import { faGoogle } from "@fortawesome/free-brands-svg-icons";
import { useNavigate } from "react-router-dom";
import {
	useTable,
	useFlexLayout,
	useExpanded,
	useResizeColumns,
} from "react-table";


const InsightsTable = (props) => {
	const data = props.data;
	const navigate = useNavigate();


	const openDetails = useCallback((resourceID, type) => {
		console.log("openDetails", resourceID, type);
		if (type === "Resource") {
			navigate("/resource/" + resourceID);
		}
	}, [navigate]);

	// Define columnDefs at the top level before any early returns
	const columnDefs = React.useMemo(() => [
		{
			accessor: "insight_group",
			id: "expander",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The insight group">
					Insight Group
				</span>
			),
			Cell: ({ row }) => {
				const toggleRowExpandedProps = row.getToggleRowExpandedProps();
				const onClick = async (event) => {
					toggleRowExpandedProps.onClick(event);
				};

				const margin = {
					paddingLeft: 36 + (row.depth * 36)
				};

				if (!row.original.insight) {
					// First level - insight group
					return (
						<span 
							{...row.getToggleRowExpandedProps()} 
							onClick={(e) => { e.stopPropagation(); onClick(e); }} 
							className="text-left text-text font-semibold cursor-pointer pt-1"
						>
							{row.isExpanded ? (
								<FontAwesomeIcon
									icon={icon({
										name: "angle-down",
										family: "sharp",
										style: "solid",
									})}
									className="text-text-2 mr-2"
								/>
							) : (
								<FontAwesomeIcon
									icon={icon({
										name: "angle-right",
										family: "sharp",
										style: "solid",
									})}
									className="text-text-2 mr-2"
								/>
							)}
							{row.original.insight_group}
						</span>
					);
				} else {
					// Second level - insight
					return (
						<div 
							style={margin}
							className="text-left text-text pt-1"
							onClick={(e) => { console.log("Clicked");e.stopPropagation(); openDetails(row.original.resource_md5, "Resource")} }
						>
							{row.original.insight}
						</div>
					);
				}
			},
			width: 500,
		},
		{
			accessor: "vendor_name",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The vendor">
					
				</span>
			),
			Cell: ({ row }) => {
				if (!row.original.insight || row.original.type === "Product") {
					return <div />;
				} else {
					return (
						<div className="text-center text-text font-semibold pt-1">
							<FontAwesomeIcon icon={faGoogle} className="text-text-2" />
						</div>
					);
				}
			},
			minWidth: 50,
			width: 50,
			maxWidth: 50,
		},
		{
			accessor: "resource_cluster",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The resource cluster">
					Cluster
				</span>
			),
			Cell: ({ row }) => {
				if (row.original.insight) {
					return (
						<div className="text-left text-text pt-1">
							{row.original.resource_cluster}
						</div>
					);
				}
			},
			width: 100,
		},
		{
			accessor: "resource_name",
			Header: () => (
				<span className="" data-tooltip-id="instantTooltip" data-tooltip-content="The resource name">
					Resource
				</span>
			),
			Cell: ({ row }) => {
				if (row.original.insight) {
					return (
						<div className="text-left text-text pt-1">
							{row.original.resource_name}
						</div>
					);
				}
			},
			width: 150,
		},
		{
			accessor: "units",
			Header: () => (
				<div className="text-right mr-2">
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The number of units associated with the insight">
						Insights
					</span>
				</div>
			),
			Cell: ({ row }) => {
				let valueClass = "text-right text-text pt-1 ";
				if (row.original.subRows && row.original.subRows.length > 0) valueClass += "font-semibold";
				return (
					<div className={valueClass}>
						{formatValue(row.original.units, "decimal", false)}
					</div>
				);
			},
			width: 75,
		},
		{
			accessor: "created_at",
			Header: () => (
				<div className="text-left">
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The date when the insight was created">
						Since
					</span>
				</div>
			),
			Cell: ({ row }) => {
				if (!row.original.insight) {
					// First level - insight group, show empty or aggregated value
					return <div className="text-left text-text pt-1" />;
				}
				// Second level - insight, show the created_at date
				const createdDate = row.original.created_at;
				if (!createdDate) {
					return <div className="text-left text-text pt-1">-</div>;
				}
				return (
					<div className="text-left text-text pt-1">
						{dayjs(createdDate).format("DD-MM-YYYY")}
					</div>
				);
			},
			width: 100,
		},
		{
			accessor: "costs",
			Header: () => (
				<div className="text-right mr-4">
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The total costs associated with the insight">
						Costs
					</span>
				</div>
			),
			Cell: ({ row }) => {
				let valueClass = "text-right text-text pt-1 mr-4 ";
				if (row.original.subRows && row.original.subRows.length > 0) valueClass += "font-semibold";
				return (
					<div className={valueClass}>
						{formatValue(row.original.costs, "currency", false)}
					</div>
				);
			},
			width: 140,
		},
	], [openDetails]);

	// Function to prepare data with two-level structure
	const prepareInsightsData = (rawData) => {
		// Ensure rawData is an array and has content
		if (!rawData || !Array.isArray(rawData) || rawData.length === 0) {
			return [];
		}
		// Group by insight_group
		const groupedData = {};
		rawData.forEach(item => {
			if (!groupedData[item.insight_group]) {
				groupedData[item.insight_group] = {
					insight_group: item.insight_group,
					units: 0,
					costs: 0,
					subRows: []
				};
			}
			groupedData[item.insight_group].units += item.units || 0;
			groupedData[item.insight_group].costs += item.costs || 0;
			groupedData[item.insight_group].subRows.push(item);
		});

		// After grouping, sort subRows (details) by costs descending within each group
		Object.values(groupedData).forEach(group => {
			group.subRows.sort((a, b) => (b.costs || 0) - (a.costs || 0));
		});

		// Sort the groups: first by costs descending, then by priority (costs, security, others alphabetically)
		const sortedGroups = Object.values(groupedData).sort((a, b) => {
			// First, sort by costs descending
			if (b.costs !== a.costs) {
				return b.costs - a.costs;
			}
			
			// If costs are the same, apply priority ordering
			const getPriority = (groupName) => {
				const lowerName = groupName.toLowerCase();
				if (lowerName.includes('cost') || lowerName.includes('costs')) return 1;
				if (lowerName.includes('security')) return 2;
				return 3; // All others
			};
			
			const priorityA = getPriority(a.insight_group);
			const priorityB = getPriority(b.insight_group);
			
			// If priorities are different, sort by priority
			if (priorityA !== priorityB) {
				return priorityA - priorityB;
			}
			
			// If priorities are the same, sort alphabetically
			return a.insight_group.localeCompare(b.insight_group);
		});

		return sortedGroups;

	};

	const preparedData = prepareInsightsData(data);

	// If no data is available, show a message
	if (!data || !Array.isArray(data) || data.length === 0) {
		return (
			<Fragment>
				<div className="text-center py-8 text-text-2">
					No insights data available
				</div>
			</Fragment>
		);
	}

	function Table({ columns, data, expandAll }) {
		const defaultExpandedRows = {};
		
		data.forEach((element, index) => {
			if (expandAll) {
				defaultExpandedRows[index] = true;
			}
		});

		const defaultColumn = React.useMemo(
			() => ({
				minWidth: 30,
				width: 150,
				maxWidth: 800,
			}),
			[]
		);

		const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
			useTable(
				{
					columns,
					data,
					defaultColumn,
					autoResetExpanded: false,
					collapseOnDataChange: false,
					initialState: { expanded: defaultExpandedRows },
				},
				useFlexLayout,
				useExpanded,
				useResizeColumns
			);

		return (
			<div
				{...getTableProps()}
				className="flex w-full cursor-default flex-auto flex-col mr-6"
			>
				<div className="py-2 pl-4 text-text-2 sticky top-0 z-50">
					{headerGroups.map((headerGroup) => {
						const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
						return (
							<div key={key} {...headerGroupProps} className="tr">
								{headerGroup.headers.map((column) => {
									const { key: columnKey, ...headerProps } = column.getHeaderProps();
									const { key: resizerKey, ...resizerProps } = column.getResizerProps();
									return (
										<div
											key={columnKey}
											{...headerProps}
											className="th uppercase text-sm"
										>
											{column.render("Header")}
											<div
												key={resizerKey}
												{...resizerProps}
												className={`resizer ${column.isResizing ? "isResizing" : ""}`}
											/>
										</div>
									);
								})}
							</div>
						);
					})}
				</div>

				<div {...getTableBodyProps()} className="bg-card border border-card-bd rounded-card">
					{rows.map((row, index) => {
						prepareRow(row);
						const { key, ...rowProps } = row.getRowProps({});

						return (
							<div
								key={key}
								{...rowProps}
								className={
									"tr border-b border-border hover:bg-primary-soft h-[77px] cursor-pointer pl-4" +
									(index === 0 ? " rounded-t-[16px]" : "") +
									(index === rows.length - 1 ? " rounded-b-[16px] border-b-0" : "")
								}
									onClick={(e) => {
									// Check if this row can be expanded
									if (row.original.subRows && row.original.subRows.length > 0) {
										row.toggleRowExpanded();
									}
								}}
							>
								{row.cells.map((cell) => {
									const { key, ...cellProps } = cell.getCellProps();
									return (
										<div
											key={key}
											{...cellProps}
											className="td truncate pl-1 pt-6 pb-4 text-sm"
										>
											{cell.render("Cell")}
										</div>
									);
								})}
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	return (
		<Fragment>
			<Tooltip />
			<Table
				className="flex-auto"
				columns={columnDefs}
				data={preparedData}
				expandAll={false}
			/>
		</Fragment>
	);
};

export default InsightsTable;
